/* eslint-disable react/display-name */
import React, { useState, useRef, useEffect } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Drawer,
  TextInputWithLabel,
  DatePicker,
  DataGrid,
  PopupMenu,
  CardsGrid
} from 'quantux-ui';
import PatientCard from '../patient-dashboard/patient-card';
import { gray, blue, salmon } from '../../theme';
import { BASE_URL, DRAWER_BREAK_POINT, titleCase, calculateAge, padZero } from '../../utils';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';

const DOCS_PER_PAGE = 50;

const defaultCellStyle = {
  font: '12px Lato',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '14px'
};

const icons = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};

const determinePiFlag = (piData, isNoPrimaryInsurance = false) => {
  if (isNoPrimaryInsurance) {
    // setPiFlag(false);
    return false;
  }
  if (Object.keys(piData).length > 0) {
    const {
      memberFirstName = '',
      memberLastName = '',
      patientRelationToMember = '',
      policyNumber = '',
      authorizationNumber = '',
      groupNumber = '',
      memberDob
    } = piData;
    const nameFlag = memberFirstName.length >= 1 && memberLastName.length >= 1;
    // const dobFlag = memberDob && !isNaN(Date.parse(memberDob));
    const dobFlag = !isNaN(Date.parse(memberDob)) && memberDob;
    const relationFlag = patientRelationToMember.length > 0;
    const policyFlag = policyNumber.length > 0;
    const authFlag = authorizationNumber.length > 0;
    const groupFlag = groupNumber.length > 0;
    // setPiFlag(!(nameFlag && dobFlag && relationFlag && policyFlag && authFlag && groupFlag));
    return !(nameFlag && dobFlag && relationFlag && policyFlag && authFlag && groupFlag)
  } else {
    // setPiFlag(true);
    return true;
  }
};

const determineSiFlag = (siData, isNoSecondaryInsurance = false) => {
  if (isNoSecondaryInsurance) {
    // setSiFlag(false);
    return false;
  }
  if (Object.keys(siData).length > 0) {
    const {
      memberFirstName = '',
      memberLastName = '',
      memberDob,
      patientRelationToMember = '',
      policyNumber = '',
      authorizationNumber = '',
      groupNumber = ''
    } = siData;
    const nameFlag = memberFirstName.length >= 1 && memberLastName.length >= 1;
    const dobFlag = memberDob && !isNaN(Date.parse(memberDob));
    const relationFlag = patientRelationToMember.length > 0;
    const policyFlag = policyNumber.length > 0;
    const authFlag = authorizationNumber.length > 0;
    const groupFlag = groupNumber.length > 0;
    // setSiFlag(!(nameFlag && dobFlag && relationFlag && policyFlag && authFlag && groupFlag));
    return (!(nameFlag && dobFlag && relationFlag && policyFlag && authFlag && groupFlag));
  } else {
    // setSiFlag(true);
    return true;
  }
};

const determineTiFlag = (tiData, isNoTertiaryInsurance = false) => {
  if (isNoTertiaryInsurance) {
    // setTiFlag(false);
    return false;
  }
  if (Object.keys(tiData).length > 0) {
    const {
      memberFirstName = '',
      memberLastName = '',
      memberDob,
      patientRelationToMember = '',
      policyNumber = '',
      authorizationNumber = '',
      groupNumber = ''
    } = tiData;
    const nameFlag = memberFirstName.length >= 1 && memberLastName.length >= 1;
    const dobFlag = memberDob && !isNaN(Date.parse(memberDob));
    const relationFlag = patientRelationToMember.length > 0;
    const policyFlag = policyNumber.length > 0;
    const authFlag = authorizationNumber.length > 0;
    const groupFlag = groupNumber.length > 0;
    // setTiFlag(!(nameFlag && dobFlag && relationFlag && policyFlag && authFlag && groupFlag));
    return !(nameFlag && dobFlag && relationFlag && policyFlag && authFlag && groupFlag);
  } else {
    // setTiFlag(true);
    return true;
  }
};

const determinePatientInfoFlag = (patientInfo) => {
  const MIN_STREET_ADDRESS_LENGTH = 5;
  const MIN_CITY_LENGTH = 3;
  const MIN_STATE_LENGTH = 2;
  const MIN_ZIP_LENGTH = 5;

  const {
    firstName = '',
    lastName = '',
    ssn1 = '',
    ssn2 = '',
    ssn3 = '',
    displayName = '',
    userName = '',
    mobile = '',
    email = '',
    homeAddress = {},
    mailingAddress = {}
  } = patientInfo;

  const {
    streetAddress1: homeStreetAddress = '',
    city: homeCity = '',
    state: homeState = '',
    zip: homeZip
  } = homeAddress;

  const {
    streetAddress1: mailingStreetAddress = '',
    city: mailingCity = '',
    state: mailingState = '',
    zip: mailingZip
  } = mailingAddress;

  // console.log(`firstName = ${firstName}`);
  // console.log(`lastName = ${lastName}`);
  // console.log(`ssn1 = ${ssn1}`);
  // console.log(`ssn2 = ${ssn2}`);
  // console.log(`ssn3 = ${ssn3}`);
  // console.log(`displayName = ${displayName}`);
  // console.log(`userName = ${userName}`);
  // console.log(`mobile = ${mobile}`);
  // console.log(`email = ${email}`);

  // console.log(`homeStreetAddress = ${homeStreetAddress}`);
  // console.log(`homeCity = ${homeCity}`);
  // console.log(`homeState = ${homeState}`);
  // console.log(`homeZip = ${homeZip}`);

  // console.log(`mailingStreetAddress = ${mailingStreetAddress}`);
  // console.log(`mailingCity = ${mailingCity}`);
  // console.log(`mailingState = ${mailingState}`);
  // console.log(`mailingZip = ${mailingZip}`);

  const nameFlag = firstName.length >= 1 && lastName.length >= 1;
  const ssnFlag = ssn1.length === 3 && ssn2.length === 2 && ssn3.length === 4;
  const patientDisplayNameFlag = displayName.length >= 2 && displayName.length <= 40;
  const patientUserNameFlag = userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0;
  const patientHomeAddressFlag =
    homeStreetAddress.length >= MIN_STREET_ADDRESS_LENGTH &&
    homeCity.length >= MIN_CITY_LENGTH &&
    homeState.length >= MIN_STATE_LENGTH &&
    homeZip.length >= MIN_ZIP_LENGTH;
  const patientMailingAddressFlag = patientInfo.mailingAddressSameAsHomeAddress || (
    mailingStreetAddress.length >= MIN_STREET_ADDRESS_LENGTH &&
    mailingCity.length >= MIN_CITY_LENGTH &&
    mailingState.length >= MIN_STATE_LENGTH &&
    mailingZip.length >= MIN_ZIP_LENGTH
  );
  const mobileFlag = isMobilePhone(mobile, 'en-US');
  const emailFlag = isEmail(email);

  // console.log(`nameFlag = ${nameFlag}`);
  // console.log(`ssnFlag = ${ssnFlag}`);
  // console.log(`patientDisplayNameFlag = ${patientDisplayNameFlag}`);
  // console.log(`patientUserNameFlag = ${patientUserNameFlag}`);
  // console.log(`patientHomeAddressFlag = ${patientHomeAddressFlag}`);
  // console.log(`patientMailingAddressFlag = ${patientMailingAddressFlag}`);
  // console.log(`mobileFlag = ${mobileFlag}`);
  // console.log(`emailFlag = ${emailFlag}`);

  // setPatientInfoFlag(!(nameFlag &&
  //   ssnFlag &&
  //   patientDisplayNameFlag &&
  //   patientUserNameFlag &&
  //   mobileFlag &&
  //   emailFlag));

  return !(nameFlag &&
    ssnFlag &&
    patientDisplayNameFlag &&
    patientUserNameFlag &&
    mobileFlag &&
    emailFlag);
};

const determineContactPersonFlag = (patientInfo) => {
  let flagValue = true;
  if (patientInfo.contactPersons && Array.isArray(patientInfo.contactPersons) && patientInfo.contactPersons.length > 0) {
    for (let i = 0; i < patientInfo.contactPersons.length; i++) {
      const cp = patientInfo.contactPersons[i];
      const firstNameFlag = cp.contactPersonFirstName && cp.contactPersonFirstName.length >= 1;
      const lastNameFlag = cp.contactPersonLastName && cp.contactPersonLastName.length >= 1;
      const relationFlag = cp.contactPersonRelationToPatient && cp.contactPersonRelationToPatient.length >= 4;
      const contactNumberFlag = cp.contactPersonContactNumber && cp.contactPersonContactNumber.length >= 10;
      flagValue = !(firstNameFlag && lastNameFlag && relationFlag && contactNumberFlag);
      if (!flagValue) {
        break;
      }
    }
    // setContactPersonFlag(flagValue);
    return flagValue;
  } else {
    // setContactPersonFlag(true);
    return true;
  }
};

const headerDefs = [
  // Patient Name
  {
    visible: true,
    dataType: 'string',
    pin: 'left',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Patient Name',
    field: 'fullName',
    key: 'fullName',
    isCustomFilter: false,
    showFilterHint: false,
    filterHint: '',
    filter: true,
    filterType: 'startsWith',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'startsWith', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['exactMatch', 'endsWith', 'advanced'],
    colStyles: { width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (data) => {
      const { fullName, piFlag, siFlag, tiFlag, patientInfoFlag, contactPersonFlag } = data;
      const insurnaceFlag = piFlag || siFlag || tiFlag;
      return (
        <React.Fragment>
          <div> {fullName} </div>
          <div style={{ color: '#0080FF', cursor: 'pointer', fontSize: '12px' }}>
            {`Insurnace = ${insurnaceFlag ? 'Not Complete' : 'Complete'}`}&nbsp;<br/>
            {`Profile = ${patientInfoFlag ? 'Not Complete' : 'Complete'}`}&nbsp;<br/>
            {`Contact Person(s) = ${contactPersonFlag ? 'Not Complete' : 'Complete'}`}
          </div>
        </React.Fragment>
      );
    }
  },
  // Gender
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 1,
    enableCellClick: true,
    text: 'Gender',
    field: 'gender',
    key: 'gender',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'contains',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'endsWith', 'advanced'],
    colStyles: { width: 135, minWidth: 135, maxWidth: 135, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  },
  // DOB
  {
    visible: true,
    dataType: 'date',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 1,
    enableCellClick: true,
    text: 'Date of Birth',
    field: 'dob',
    key: 'dob',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'dateRange',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  }
];

const today = new Date();

const ageArray = [];
for (let i = 0; i < 100; i++) {
  ageArray.push(i + 1);
}

const PatientsDataGrid = (props) => {
  const [showAddPatientDrawer, setShowAddPatientDrawer] = useState(false);
  const [patientFirstName, setPatientFirstName] = useState('');
  const [patientMiddleName, setPatientMiddleName] = useState('');
  const [patientLastName, setPatientLastName] = useState('');
  const [ssn1, setSSN1] = useState('');
  const [ssn2, setSSN2] = useState('');
  const [ssn3, setSSN3] = useState('');
  const [gender, setGender] = useState('');
  const [showGenderAlert, setShowGenderAlert] = useState(true);
  const [isDobDatePristine, setIsDobDatePristine] = useState(true);
  const [showDobAlert, setShowDobAlert] = useState(true);
  const [dobDate, setDobDate] = useState(today.getDate());
  const [dobMonth, setDobMonth] = useState(today.getMonth());
  const [dobYear, setDobYear] = useState(today.getFullYear());
  const [canAddPatient, setCanAddPatient] = useState(false);
  const [canAddTempPatient, setCanAddTempPatient] = useState(false);
  const [showForm1, setShowForm1] = useState(true);
  const [showForm2, setShowForm2] = useState(false);
  const [showTmpForm, setShowTmpForm] = useState(false);
  const [patientAge, setPatientAge] = useState(0);
  const [isDuplicatePatient, setIsDuplicatePatient] = useState(true);
  // Tmp Patient
  const [showAgeMenu, setShowAgeMenu] = useState(false);
  const [age, setAge] = useState('');
  // Grid
  const [isMobileLayout, setMobileLayout] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [gridHeaderDefs, setGridHeaderDefs] = useState(headerDefs);
  const [gridData, setGridData] = useState([]);
  const [gridWidth, setGridWidth] = useState(window.innerWidth <= 768 ? window.innerWidth - 16 : 572);
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 150);
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // Cards Grid
  const [showCardsGrid, setShowCardsGrid] = useState(true);

  // Refs
  const gridParentRef = useRef();
  const ageMenuRef = useRef();

  // Router related
  const history = useHistory();

  // Window event handlers
  const handleWindowResize = () => {
    console.log('Handle resize!');
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth = gridParentRef.current.getBoundingClientRect().width;
      // console.log(`gridParentRef width = ${gridParentRefWidth}`);
      // const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.75 * gridParentRefWidth);
      const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.98 * gridParentRefWidth);
      // console.log(`GridWidth = ${tmp}`);
      if (showCardsGrid) {
        console.log('cards grid');
        setGridWidth(Math.min(tmp, 1111));
      } else {
        console.log('rows grid');
        setGridWidth(Math.min(tmp, 572));
      }
      setGridHeight(window.innerHeight - 134);
      setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };

  // Get Data
  const getData = async () => {
    setShowLoader(true);
    const formData = new FormData();
    formData.append('skip', (currentPage - 1) * DOCS_PER_PAGE);
    formData.append('limit', DOCS_PER_PAGE);
    formData.append('filters', JSON.stringify(filters));
    formData.append('sorting', JSON.stringify(sorting));

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    const dataPromise = fetch(`${BASE_URL}/patients/all`, requestOptions);
    const countPromise = fetch(`${BASE_URL}/patients/count`, requestOptions);

    const response = await Promise.all([dataPromise, countPromise])
      .catch(err => {
        console.log('Error getting users grid data and count');
        console.log(err);
      })

    if (response) {
      const patientsDataResponse = await response[0].json();
      const patientsCountResponse = await response[1].json();

      if (patientsDataResponse && patientsDataResponse.success) {
        const data = [];
        console.log(patientsDataResponse.patients);
        for (let i = 0; i < patientsDataResponse.patients.length; i++) {
          const activeRecord = patientsDataResponse.patients[i];
          const fullName = titleCase(`${activeRecord.lastName}, ${activeRecord.firstName}`);
          // Flags
          const piFlag = determinePiFlag(activeRecord.primaryInsurance || {}, activeRecord.isNoPrimaryInsurance);
          const siFlag = determineSiFlag(activeRecord.secondaryInsurance || {}, activeRecord.isNoSecondaryInsurance);
          const tiFlag = determineTiFlag(activeRecord.tertiaryInsurance || {}, activeRecord.isNoTertiaryInsurance);
          const patientInfoFlag = determinePatientInfoFlag(activeRecord);
          const contactPersonFlag = determineContactPersonFlag(activeRecord);

          const gender = titleCase(activeRecord.gender || '');
          // const localDob = new Date(activeRecord.dob);
          const tmp1 = activeRecord.dob.split('T');
          const tmp2 = tmp1[0].split('-');
          const _year = tmp2[0];
          const _month = tmp2[1];
          const _date = tmp2[2];
          data.push({
            id: activeRecord._id,
            fullName: {
              fullName,
              piFlag,
              siFlag,
              tiFlag,
              patientInfoFlag,
              contactPersonFlag
            },
            userName: activeRecord.userName,
            gender,
            ssn1: activeRecord.ssn1,
            ssn2: activeRecord.ssn2,
            ssn3: activeRecord.ssn3,
            // dob: `${padZero(localDob.getMonth() + 1)}/${padZero(localDob.getDate())}/${localDob.getFullYear()}`
            // dob: `${padZero(localDob.getMonth())}/${padZero(localDob.getDate())}/${localDob.getFullYear()}`
            dob: `${_month}/${_date}/${_year}`
          });
        }
        // console.log(data);
        setGridData(data);
        setShowLoader(false);
      }

      if (patientsCountResponse && patientsCountResponse.success) {
        console.log(`[Debug] Total users count = ${patientsCountResponse.usersCount}`);
        setDocsCount(patientsCountResponse.patientsCount);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('orientationchange', handleWindowResize);
    getData();
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    handleWindowResize();
    // debouncedHandleWindowResize(showCardsGrid);
  }, [showCardsGrid]);

  useEffect(() => {
    if (!showTmpForm) {
      const nameFlag = patientFirstName.length > 0 && patientLastName.length > 0;
      const genderFlag = gender === 'male' || gender === 'female' || gender === 'other';
      setCanAddPatient(nameFlag && genderFlag && !showDobAlert);
    } else {
      const nameFlag = patientFirstName.length > 0 && patientLastName.length > 0;
      const genderFlag = gender === 'male' || gender === 'female';
      const ageFlag = age !== '';
      setCanAddTempPatient(nameFlag && genderFlag && ageFlag);
    }
  }, [
    patientFirstName,
    patientLastName,
    gender,
    showDobAlert,
    age
  ]);

  useEffect(() => {
    setPatientAge(calculateAge(`${padZero(dobMonth + 1)}/${padZero(dobDate)}/${dobYear}`));
    // const dob = new Date(dobYear, dobMonth, dobDate);
    // const d2 = new Date();
    // const DAYS_IN_YEAR = 365.26;
    // // const age = (d2.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24 * 365.26);
    // let age = 0;
    // const ageInDays = (d2.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24);

    // if (ageInDays <= DAYS_IN_YEAR) {
    //   if (ageInDays <= 31) {
    //     age = `${Math.floor(ageInDays)}d`;
    //   } else {

    //     age = `${Math.floor(ageInDays/)}m`;
    //   }
    // } else {
    //   age = `${Math.floor(ageInDays / DAYS_IN_YEAR)}Y`;
    // }

    // // setPatientAge(age.toFixed(0));
    // console.log(`PATIENT AGE = ${age}`);
    // setPatientAge(Math.floor(age));
  }, [dobDate, dobMonth, dobYear]);

  const styles = StyleSheet.create({
    drawerContainer: {
      background: gray[800],
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerBanner: {
      position: 'relative',
      padding: '8px',
      flexShrink: '0'
    },
    drawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerContentArea: {
      // height: 'calc(100vh - 186px)',
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1'
    },
    drawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      background: blue[500],
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    drawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    drawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerUserCardText: {
      fontSize: '12px'
    },
    profileTextAreaContainer: {
      paddingRight: '8px'
    },
    profileTextArea: {
      width: '100%',
      background: gray[900],
      height: '100px',
      color: gray[100],
      border: 'none',
      resize: 'none',
      overflow: 'auto',
      fontFamily: 'Lato',
      padding: '8px',
      ':focus': {
        outlineColor: 'transparent',
        outlineStyle: 'none'
      }
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      // position: 'absolute',
      // top: 'calc(100vh - 56px)',
      flexShrink: '0'
    },
    imageUploaderContainer: {
      position: 'relative',
      maxHeight: '454px'
    },
    imageUploadLoader: {
      position: 'relative',
      height: '454px',
      top: '-400px',
      background: gray[800]
    },
    errMsg: {
      color: '#FF6060',
      fontSize: '12px'
    },
    sectionHeading: {
      color: blue[200],
      fontSize: '14px'
    },
    ssnSeparator: {
      padding: '15px 8px 0 8px'
    },
    pressedBtnsContainer: {
      display: 'flex',
      padding: '2px',
      border: `1px solid ${gray[400]}`,
      borderRadius: '5px'
    },
    pressedBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      background: gray[400],
      color: showGenderAlert ? salmon[500] : gray[200],
      height: '24px',
      width: '50px',
      borderRadius: '5px',
      cursor: 'pointer'
    },
    activePressedBtn: {
      color: 'white',
      background: blue[500]
    },
    gridParent: {
      overflow: 'hidden',
      padding: '0 8px'
    },
    gridContainer: {
      // maxWidth: '972px', // was 914px
      // width: '100%',
      // width: '550px',
      width: `${gridWidth}px`,
      overflow: 'hidden'
      // height: showCardsGrid ? `${window.innerHeight - 90}px` : ''
    },
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      height: '40px',
      border: '1px solid black',
      borderBottom: 'none',
      paddingLeft: '4px',
      background: gray[500],
      width: '100%'
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    },
    gridEllipsisMenuParent: {
      position: 'fixed',
      height: '64px',
      width: '140px',
      background: 'black',
      zIndex: '100'
    },
    gridEllipsisMenuContainer: {
      minWidth: '140px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '138px',
      paddingLeft: '8px',
      background: '#103A60',
      ':hover': {
        background: '#0066CC'
      }
    },
    popupMenuContainer: {
      background: '#083050',
      width: '100px',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.5)',
      height: '200px',
      overflowY: 'auto'
    },
    menuItem: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      color: '#5080B0',
      paddingLeft: '8px'
    },
    menuItemIconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '40px',
      width: '40px'
    },
    menuItemIcon: {
      fontSize: '20px'
    }
  });

  const closeShowPatientDrawer = () => {
    setShowAddPatientDrawer(false);
    setShowAddPatientDrawer(false);
    setPatientFirstName('');
    setPatientMiddleName('');
    setPatientLastName('');
    // setSSN1('???');
    // setSSN2('??');
    // setSSN3('????');
    setGender('');
    setShowGenderAlert(true);
    setIsDobDatePristine(true);
    setShowDobAlert(true);
    setDobDate(today.getDate());
    setDobMonth(today.getMonth());
    setDobYear(today.getFullYear());
    setShowForm1(true);
    setShowForm2(false);
    setShowTmpForm(false);
    setIsDuplicatePatient(true);
  };

  // #region Create Patient Drawer Event Handlers
  const handleOnEditPatientFirstName = (e) => {
    const { value } = e.target;
    setPatientFirstName(value);
  };

  const handleOnEditPatientMiddleName = (e) => {
    const { value } = e.target;
    setPatientMiddleName(value);
  };

  const handleOnEditPatientLastName = (e) => {
    const { value } = e.target;
    setPatientLastName(value);
  };

  const handleOnEditSSN1 = (e) => {
    const { value } = e.target;
    const val = parseInt(value) + '';
    if (!isNaN(val)) {
      if (val.length > 3) {
        setSSN1(val.substring(0, 3));
      } else {
        setSSN1(val);
      }
    } else {
      setSSN1('');
    }
  };

  const handleOnEditSSN2 = (e) => {
    const { value } = e.target;
    const val = parseInt(value) + '';
    if (!isNaN(val)) {
      if (val.length > 2) {
        setSSN2(val.substring(0, 2));
      } else {
        setSSN2(val);
      }
    } else {
      setSSN2('');
    }
  };

  const handleOnEditSSN3 = (e) => {
    const { value } = e.target;
    const val = parseInt(value) + '';
    if (!isNaN(val)) {
      if (val.length > 4) {
        setSSN3(val.substring(0, 4));
      } else {
        setSSN3(val);
      }
    } else {
      setSSN3('');
    }
  };

  const handleOnChangeGender = (val) => {
    setGender(val);
    setShowGenderAlert(false);
  };

  const handleOnCLickNext = () => {
    const formData = new FormData();
    formData.append('firstName', patientFirstName);
    formData.append('middleName', patientMiddleName);
    formData.append('lastName', patientLastName);
    formData.append('dob', `${dobMonth + 1}/${dobDate}/${dobYear}`);
    formData.append('ssn3', ssn3.replaceAll('?', ''));

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/checkDuplicate`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          setIsDuplicatePatient(res.isDuplicate);
          setShowForm1(false);
          setShowForm2(true);
          setShowTmpForm(false);
        } else {
          setIsDuplicatePatient(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnCreateTempPatient = () => {
    const formData = new FormData();
    formData.append('firstName', patientFirstName);
    formData.append('middleName', patientMiddleName);
    formData.append('lastName', patientLastName);
    formData.append('age', age);
    formData.append('gender', gender);
    formData.append('ssn1', ssn1.replaceAll('?', ''));
    formData.append('ssn2', ssn2.replaceAll('?', ''));
    formData.append('ssn3', ssn3.replaceAll('?', ''));

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/generateTempPatient`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          setShowForm1(false);
          setShowForm2(false);
          setShowTmpForm(false);
          closeShowPatientDrawer();
          getData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnCLickCreatePatientProfile = () => {
    const formData = new FormData();
    formData.append('firstName', patientFirstName);
    formData.append('middleName', patientMiddleName);
    formData.append('lastName', patientLastName);
    formData.append('ssn1', ssn1.replaceAll('?', ''));
    formData.append('ssn2', ssn2.replaceAll('?', ''));
    formData.append('ssn3', ssn3.replaceAll('?', ''));
    formData.append('gender', gender);

    // const dobIsoString = new Date(dobYear, dobMonth, dobDate).toISOString();
    // console.log(`dobIsoString = ${dobIsoString}`);
    // formData.append('dob', dobIsoString);

    // const db1 = new Date(dobYear, dobMonth, dobDate).toISOString();
    // console.log(db1);
    // const tmp = db1.split('T');
    // yyyy-mm-ddT00:00:00.000Z
    formData.append('dob', `${dobYear}-${padZero(dobMonth + 1)}-${padZero(dobDate)}` + 'T00:00:00.000Z');

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/createPatient`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          console.log('Patient added successfully!');
          closeShowPatientDrawer();
          getData();
        } else {
          console.error('Failed to create patient profile!');
        }
      })
      .catch(error => console.log('error', error));
  };
  // #endregion

  // #region Data Grid - Event handlers

  const handleOnClickResetFilters = () => { };
  const handleOnLoadPage = () => { };
  const handleOnApplyFilter = () => { };
  const handleFilterTypeUpdate = () => { };
  const handleOnSortOrderChanged = () => { };
  const handleOnSelectItemChange = () => { };
  const handleOnSelectAllChange = () => { };

  const handleOnClickCell = (col, value, recordId) => {
    if (col === 'fullName') {
      history.push(`/patient/${recordId}/info`);
    }
  };

  // #endregion

  // #region Patient profile parts
  const patientCreationFormPart1 = (
    <>
      {/* First, Middle and Last name */}
      <div className='pl-2 pr-2 fx mt-1'>
        {/* First Name */}
        <div>
          <TextInputWithLabel
            value={patientFirstName}
            titleText="First Name"
            titleFontSize={12}
            isCompactLayout={true}
            titleColor={gray[200]}
            textColor="white"
            selectionColor="white"
            selectionBackground="#0080FF"
            spellcheck={false}
            width={113}
            onChange={handleOnEditPatientFirstName}
          />
        </div>
        {/* Middle Name */}
        <div className='ml-2'>
          <TextInputWithLabel
            value={patientMiddleName}
            titleText="Middle Name"
            titleFontSize={12}
            isCompactLayout={true}
            titleColor={gray[200]}
            textColor="white"
            selectionColor="white"
            selectionBackground="#0080FF"
            spellcheck={false}
            width={113}
            onChange={handleOnEditPatientMiddleName}
          />
        </div>
        {/* Last Name */}
        <div className='ml-2'>
          <TextInputWithLabel
            value={patientLastName}
            titleText="Last Name"
            titleFontSize={12}
            isCompactLayout={true}
            titleColor={gray[200]}
            textColor="white"
            selectionColor="white"
            selectionBackground="#0080FF"
            spellcheck={false}
            width={113}
            onChange={handleOnEditPatientLastName}
          />
        </div>
      </div>
      {/* SSN & DOB */}
      <div className='pl-2 pr-2 mt-4'>
        <div className={css(styles.sectionHeading)}>
          SSN (if available)
        </div>
        <div
          className='fx'
          style={{
            alignItems: 'flex-end',
            marginTop: '-10px'
          }}
        >
          <div className='fx'>
            <TextInputWithLabel
              value={ssn1}
              titleText=''
              placeholderColor={gray[200]}
              placeholder='???'
              titleFontSize={12}
              isCompactLayout={false}
              titleColor={gray[200]}
              textColor="white"
              selectionColor="white"
              selectionBackground="#0080FF"
              spellcheck={false}
              width={30}
              onChange={handleOnEditSSN1}
            />
            <div className={css(styles.ssnSeparator)}>-</div>
            <TextInputWithLabel
              value={ssn2}
              titleText=''
              placeholderColor={gray[200]}
              placeholder='??'
              titleFontSize={12}
              isCompactLayout={true}
              titleColor={gray[200]}
              textColor="white"
              selectionColor="white"
              selectionBackground="#0080FF"
              spellcheck={false}
              width={26}
              onChange={handleOnEditSSN2}
            />
            <div className={css(styles.ssnSeparator)}>-</div>
            <TextInputWithLabel
              value={ssn3}
              titleText=''
              placeholderColor={gray[200]}
              placeholder='????'
              titleFontSize={12}
              isCompactLayout={true}
              titleColor={gray[200]}
              textColor="white"
              selectionColor="white"
              selectionBackground="#0080FF"
              spellcheck={false}
              width={52}
              onChange={handleOnEditSSN3}
            />
          </div>
          {/* DOB */}
          <div className='ml-3 fx'>
            <div className={`pr-2 ${css(styles.sectionHeading)}`}>
              Date of Birth
            </div>
            <DatePicker
              left={0}
              right={-10}
              date={dobDate}
              month={dobMonth}
              year={dobYear}
              isPrisitine={isDobDatePristine}
              color={showDobAlert ? salmon[500] : blue[400]}
              noUnderline
              onChangeDate={(year, month, date) => {
                setShowDobAlert(false);
                setDobDate(date);
                setDobMonth(month);
                setDobYear(year);
                setIsDobDatePristine(false);
              }}
            />
          </div>
        </div>
      </div>
      {/* Gender */}
      <div
        className='fx mt-4 pl-2 pr-2'
        style={{ alignItems: 'center' }}
      >
        <div className={`mr-2 ${css(styles.sectionHeading)}`}>
          Biological Sex
        </div>
        {/* Gender */}
        <div className={css(styles.pressedBtnsContainer)}>
          <div
            className={css(styles.pressedBtn, gender === 'male' && styles.activePressedBtn)}
            style={{ marginRight: '2px' }}
            onClick={() => handleOnChangeGender('male')}
          >
            Male
          </div>
          <div
            className={css(styles.pressedBtn, gender === 'female' && styles.activePressedBtn)}
            style={{ marginRight: '2px' }}
            onClick={() => handleOnChangeGender('female')}
          >
            Female
          </div>
          {/* <div
            className={css(styles.pressedBtn, gender === 'other' && styles.activePressedBtn)}
            onClick={() => handleOnChangeGender('other')}
          >
            Other
          </div> */}
        </div>
      </div>
      {/* Separator */}
      <div
        className='mt-4'
        style={{
          height: '1px',
          background: blue[700]
        }}
      />
      <div className='mt-4 pl-2 pr-2'>
        <Button
          btnType='primary'
          isDisabled={!canAddPatient}
          onClick={handleOnCLickNext}
          style={{
            width: '100%'
          }}
        >
          Next
        </Button>
      </div>
      {!showTmpForm &&
        (
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            If unknow patient,&nbsp;
            <span
              className='cursor__pointer'
              style={{ color: blue[500] }}
              onClick={() => {
                setShowForm1(false);
                setShowForm2(false);
                setShowTmpForm(true);
                setPatientFirstName('John');
                setPatientLastName(`Doe_${Math.random().toString(36).slice(7)}`);
              }}
            >
              create temporary profile
            </span>
          </div>
        )
      }
    </>
  );

  const patientCreationFormPart2 = (
    <>
      {/* Patient Card */}
      <PatientCard
        data={{
          patientFirstName,
          patientLastName,
          ssn1,
          ssn2,
          ssn3,
          dobMonth,
          dobDate,
          dobYear,
          patientAge,
          gender
        }}
      />

      {/* Duplicate warning message */}
      {isDuplicatePatient &&
        <div
          className='fx mt-4 pl-3 pr-3'
          style={{ color: '#FFFF00' }}
        >
          <div style={{ fontSize: '24px' }}>
            <i className='fa fa-exclamation-triangle' />
          </div>
          <div className='ml-3'>
            Atleast 1 other patient has the same information you have entered.
          </div>
        </div>
      }
      {/* Footer */}
      <div
        className='fx'
        style={{
          marginTop: '100px',
          padding: '0 8px',
          borderTop: `1px solid ${blue[700]}`,
          borderBottom: `1px solid ${blue[700]}`,
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '50px'
        }}
      >
        <Button
          btnType='primary'
          isTextBtn
          onClick={() => {
            setShowForm1(true);
            setShowForm2(false);
            setShowTmpForm(false);
          }}
        >
          &lt; Back
        </Button>
        <Button
          btnType='primary'
          isDisabled={isDuplicatePatient}
          style={{ width: '104px' }}
          onClick={handleOnCLickCreatePatientProfile}
        >
          Next
        </Button>
      </div>
    </>
  );

  const tmpForm = (
    <>
      {/* First, Middle and Last name */}
      <div className='pl-2 pr-2 fx mt-1'>
        {/* First Name */}
        <div>
          <TextInputWithLabel
            value={patientFirstName}
            titleText="First Name"
            titleFontSize={12}
            isCompactLayout={true}
            titleColor={gray[200]}
            textColor="white"
            selectionColor="white"
            selectionBackground="#0080FF"
            spellcheck={false}
            width={113}
            onChange={handleOnEditPatientFirstName}
          />
        </div>
        {/* Middle Name */}
        <div className='ml-2'>
          <TextInputWithLabel
            value={patientMiddleName}
            titleText="Middle Name"
            titleFontSize={12}
            isCompactLayout={true}
            titleColor={gray[200]}
            textColor="white"
            selectionColor="white"
            selectionBackground="#0080FF"
            spellcheck={false}
            width={113}
            onChange={handleOnEditPatientMiddleName}
          />
        </div>
        {/* Last Name */}
        <div className='ml-2'>
          <TextInputWithLabel
            value={patientLastName}
            titleText="Last Name"
            titleFontSize={12}
            isCompactLayout={true}
            titleColor={gray[200]}
            textColor="white"
            selectionColor="white"
            selectionBackground="#0080FF"
            spellcheck={false}
            width={113}
            onChange={handleOnEditPatientLastName}
          />
        </div>
      </div>
      {/* SSN & Est Age */}
      <div className='pl-2 pr-2 mt-4'>
        <div className={css(styles.sectionHeading)}>
          SSN (if available)
        </div>
        <div
          className='fx'
          style={{
            alignItems: 'flex-end',
            marginTop: '-10px'
          }}
        >
          <div className='fx'>
            <TextInputWithLabel
              value={ssn1}
              titleText=''
              placeholderColor={gray[200]}
              placeholder='???'
              titleFontSize={12}
              isCompactLayout={false}
              titleColor={gray[200]}
              textColor="white"
              selectionColor="white"
              selectionBackground="#0080FF"
              spellcheck={false}
              width={30}
              onChange={handleOnEditSSN1}
            />
            <div className={css(styles.ssnSeparator)}>-</div>
            <TextInputWithLabel
              value={ssn2}
              titleText=''
              placeholderColor={gray[200]}
              placeholder='??'
              titleFontSize={12}
              isCompactLayout={true}
              titleColor={gray[200]}
              textColor="white"
              selectionColor="white"
              selectionBackground="#0080FF"
              spellcheck={false}
              width={26}
              onChange={handleOnEditSSN2}
            />
            <div className={css(styles.ssnSeparator)}>-</div>
            <TextInputWithLabel
              value={ssn3}
              titleText=''
              placeholderColor={gray[200]}
              placeholder='????'
              titleFontSize={12}
              isCompactLayout={true}
              titleColor={gray[200]}
              textColor="white"
              selectionColor="white"
              selectionBackground="#0080FF"
              spellcheck={false}
              width={52}
              onChange={handleOnEditSSN3}
            />
          </div>
          {/* Est Age */}
          <div
            className='fx'
            style={{ position: 'relative', marginLeft: '52px' }}
            ref={ageMenuRef}
          >
            <div
              className={`pt-2 pr-2 ${css(styles.sectionHeading)}`}
              onClick={() => { setShowAgeMenu(true); }}
            >
              Est Age&nbsp;
              <span>
                {age === '' ? '?' : age}
              </span>
              <span
                style={{
                  color: age === '' ? salmon[500] : gray[200]
                }}
              >
                &nbsp;Years <i className='ml-2 fa fa-caret-down' />
              </span>
            </div>
            <PopupMenu
              top={28}
              right={1}
              visible={showAgeMenu}
              parent={ageMenuRef}
              position="absolute"
              onOutsideClick={() => setShowAgeMenu(false)}
            >
              <div className={css(styles.popupMenuContainer)}>
                {ageArray.map((el) => (
                  <div
                    key={`${el}_age_item`}
                    className={`m-item ${css(styles.menuItem)}`}
                    onClick={() => {
                      setAge(el);
                      setShowAgeMenu(false);
                    }}
                  >
                    {el}
                  </div>
                ))}
              </div>
            </PopupMenu>
            {/* <NumberInput
              value={age}
              unit=' year(s)'
              width={75}
              handleOnIncrementClick={() => setAge(age + 1)}
              handleOnDecrementClick={() => setAge(age - 1)}
              handleOnChangeValue={(e) => {
                const { value } = e.target;
              }}
            /> */}
          </div>
        </div>
      </div>
      {/* Gender */}
      <div
        className='fx mt-4 pl-2 pr-2'
        style={{ alignItems: 'center' }}
      >
        <div className={`mr-2 ${css(styles.sectionHeading)}`}>
          Biological Sex
        </div>
        {/* Gender */}
        <div className={css(styles.pressedBtnsContainer)}>
          <div
            className={css(styles.pressedBtn, gender === 'male' && styles.activePressedBtn)}
            style={{ marginRight: '2px' }}
            onClick={() => {
              handleOnChangeGender('male');
              if (showTmpForm) {
                setPatientFirstName('John');
              }
            }}
          >
            Male
          </div>
          <div
            className={css(styles.pressedBtn, gender === 'female' && styles.activePressedBtn)}
            style={{ marginRight: '2px' }}
            onClick={() => {
              handleOnChangeGender('female');
              if (showTmpForm) {
                setPatientFirstName('Jane');
              }
            }}
          >
            Female
          </div>
          {/* <div
            className={css(styles.pressedBtn, gender === 'other' && styles.activePressedBtn)}
            onClick={() => handleOnChangeGender('other')}
          >
            Other
          </div> */}
        </div>
      </div>
      {/* Separator */}
      <div
        className='mt-4'
        style={{
          height: '1px',
          background: blue[700]
        }}
      />
      <div className='mt-4 pl-2 pr-2'>
        <Button
          btnType='primary'
          isDisabled={!canAddTempPatient}
          onClick={handleOnCreateTempPatient}
          style={{
            width: '100%'
          }}
        >
          Next
        </Button>
      </div>
    </>
  );
  // #endregion

  return (
    <div
      className={css(styles.gridParent)}
      ref={gridParentRef}
    >
      {/* Create Patient Drawer */}
      {showAddPatientDrawer &&
        <Drawer
          placement="right"
          visible={showAddPatientDrawer}
          toggleDrawer={closeShowPatientDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <svg
                  width="24"
                  height="24"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ fill: 'inherit' }}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2 0a2 2 0 00-2 2v20a2 2 0 002 2h20a2 2 0 002-2V2a2 2 0 00-2-2H2zm14.875 7.125c0 2.49-2.01 4.5-4.5 4.5s-4.5-2.01-4.5-4.5 2.01-4.5 4.5-4.5 4.5 2.01 4.5 4.5zm-13.5 12c0-3 6-4.65 9-4.65s9 1.65 9 4.65v1.5h-18v-1.5z"
                    style={{ fill: gray[200] }}
                  />
                </svg>
                <div className='ml-2'>
                  {showTmpForm
                    ? 'Create Temporary Patient Profile'
                    : 'Create Patient Profile'
                  }
                </div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={closeShowPatientDrawer}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.drawerContentArea)}>
              {showForm1 && patientCreationFormPart1}
              {showForm2 && patientCreationFormPart2}
              {showTmpForm && tmpForm}
            </div>
          </div>
        </Drawer>
      }
      {/* Patient's Data Grid */}
      <div className={css(styles.gridContainer)}>
        {/* Grid Toolbar  */}
        <div className={css(styles.gridToolbar)}>
          {/* Cards Grid */}
          <div
            className={css(styles.gridToolbarIcon)}
            onClick={() => setShowCardsGrid(!showCardsGrid)}
          >
            {/* <i className='fa fa-lg fa-plus-square' /> */}
            <i className={`fa fa-lg ${showCardsGrid ? 'fa-bars' : 'fa-th-large'}`} />
          </div>
          {/* Add Patient */}
          <div
            className={css(styles.gridToolbarIcon)}
            onClick={() => setShowAddPatientDrawer(true)}
          >
            <i className='fa fa-lg fa-plus-square' />
          </div>
          {/* Reset button */}
          <div
            className={css(styles.gridToolbarIcon)}
            onClick={handleOnClickResetFilters}
          >
            <i className='fa fa-lg fa-refresh' />
          </div>
        </div>
        {/* Data Grid / Cards Grid */}
        {!showCardsGrid
          ? <DataGrid
              loader={showLoader}
              icons={icons}
              headerDefs={gridHeaderDefs}
              rowData={gridData}
              filters={filters}
              gridWidth={gridWidth} // was 550
              gridHeight={gridHeight}
              gridHeaderHeight={48}
              gridFooterHeight={0}
              gridContainerHeight='730px'
              rowHeight={80}
              rowsPerPage={DOCS_PER_PAGE}
              defaultCellStyle={defaultCellStyle}
              showFooter={false}
              showPager
              showEllipsisCol
              enableHover
              totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
              currentPage={currentPage}
              isMobileLayout={isMobileLayout}
              canSelectRows={false}
              onLoadPage={handleOnLoadPage}
              onApplyFilter={handleOnApplyFilter}
              onFilterTypeUpdate={handleFilterTypeUpdate}
              onSort={handleOnSortOrderChanged}
              onClickCell={handleOnClickCell}
              onSelectItemChange={handleOnSelectItemChange}
              onSelectAllChange={handleOnSelectAllChange}
              onPopupMenu={() => { }}
              onColFilterToggle={() => { }}
              onColPinChange={() => {
              }}
              onFilterChange={() => { console.warn('Handler not set for onFilterChange prop.'); }}
              onResetCol={() => { console.warn('Handler not set for onResetCol prop.'); }}
              onResize={() => { console.warn('Handler not set for onResize prop.'); }}
              onColVisibilityChange={() => { console.warn('Handler not set for onColVisibilityChange prop.'); }}
              onChangeTextAlignment={() => { console.warn('Handler not set for onChangeTextAlignment prop.'); }}
              onResetGrid={() => { console.warn('Handler not set for onResetGrid prop.'); }}
              onColConfig={() => { console.warn('Handler not set for onColConfig prop.'); }}
            />
          : <CardsGrid
              rowData={gridData}
              gridWidth={gridWidth}
              gridHeight={gridHeight}
              loader={showLoader}
              style={{ height: 'calc(100% - 73px)', overflowY: 'auto' }}
              cardHeight='72px'
              cardWidth='359px'
              totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
              currentPage={currentPage}
              onLoadPage={handleOnLoadPage}
              cardRenderer={(data) => {
                const {
                  fullName: nameData,
                  ssn1 = '',
                  ssn2 = '',
                  ssn3 = '',
                  dob,
                  gender,
                  userName = ''
                } = data;

                const {
                  fullName,
                  piFlag,
                  siFlag,
                  tiFlag,
                  patientInfoFlag,
                  contactPersonFlag
                } = nameData;

                const name = fullName.split(',');
                const tmp1 = dob.split('/');
                const _month = tmp1[0];
                const _date = tmp1[1];
                const _year = tmp1[2];
                const patientAge = calculateAge(`${_month}/${_date}/${_year}`);

                return (
                  <div
                    style={{ width: '359px' }}
                    key={Math.random().toString(36).slice(2)}
                    onClick={() => {
                      console.log(data);
                      history.push(`/patient/${data.id}/info`);
                    }}
                  >
                    <PatientCard
                      className=''
                      data={{
                        patientFirstName: name[1] || '',
                        patientLastName: name[0] || '',
                        insuranceFlag: piFlag || siFlag || tiFlag,
                        profileFlag: patientInfoFlag,
                        contactFlag: contactPersonFlag,
                        ssn1,
                        ssn2,
                        ssn3,
                        // dobMonth: localDob.getMonth(),
                        // dobDate: localDob.getDate(),
                        // dobYear: localDob.getFullYear(),
                        dobMonth: _month,
                        dobDate: _date,
                        dobYear: _year,
                        patientAge,
                        gender: gender && gender.toLowerCase() === 'male' ? 'M' : 'F',
                        userName
                      }}
                    />
                  </div>
                );
              }}
            />
        }
      </div>
    </div>
  );
};

export default PatientsDataGrid;
